import { EditingChip, RemoteCursors, useRemoteEditors, type UiRoom } from '@flobi/collab-client/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CanvasHandle } from '../useEditorCanvas';

/**
 * Other people on the canvas: their pointers, and the name on what they are typing in.
 *
 * The collab kit writes every move straight to the DOM, so this re-renders when the view moves (a
 * new `toScreen` places the pointers again) and when somebody starts or stops typing in a text —
 * never for a pointer moving. Their selections are outlined by the canvas itself, in the frame.
 * Nothing here is drawn while nobody else is in the file.
 */
export function RemotePeople({ room, canvas, pageId }: { room: UiRoom; canvas: Pick<CanvasHandle, 'onViewChange' | 'viewKey' | 'screenAt' | 'boxOf'>; pageId: string }) {
  const [moved, setMoved] = useState(0);
  const seen = useRef(canvas.viewKey());
  useEffect(
    () =>
      canvas.onViewChange(() => {
        const key = canvas.viewKey();
        if (key === seen.current) return;
        seen.current = key;
        setMoved((count) => count + 1);
      }),
    [canvas],
  );
  // A new function for every move of the view, which is how the kit is told to place the pointers again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toScreen = useCallback((point: { x: number; y: number; space: string }) => (point.space === pageId ? canvas.screenAt(point) : null), [canvas, pageId, moved]);
  const typing = useRemoteEditors(room);
  return (
    <>
      <RemoteCursors room={room} toScreen={toScreen} />
      {[...typing.keys()].map((id) => {
        const box = canvas.boxOf(id);
        if (!box) return null;
        return (
          <div key={id} className="pointer-events-none absolute z-10" style={{ left: box.left, top: Math.max(0, box.top - 22) }}>
            <EditingChip room={room} entityId={id} />
          </div>
        );
      })}
    </>
  );
}
